import { useEffect, useState, useCallback, useRef } from 'react';
import { Moon } from 'lucide-react';
import { Header } from './components/Header';
import { LayoutToggle } from './components/LayoutToggle';
import { PrayerCard } from './components/PrayerCard';
import { PrayerTable } from './components/PrayerTable';
import { AllPrayersList } from './components/AllPrayersList';
import { MobileView } from './components/MobileView';
import { SinglePrayerView } from './components/SinglePrayerView';
import { InstallPrompt } from './components/InstallPrompt';
import { NotificationPermission } from './components/NotificationPermission';
import { KioskControls } from './components/KioskControls';
import { ShareButton } from './components/ShareButton';
import { ShareLandingPage } from './components/ShareLandingPage';
import { PreferencesPage } from './components/PreferencesPage';
import { CRMPage } from './components/CRMPage';
import { LoginPage } from './components/LoginPage';
import { ResetPasswordPage } from './components/ResetPasswordPage';
import { EventCountdown } from './components/EventCountdown';
import { ActiveEventDaily } from './components/ActiveEventDaily';
import { EventsToggle } from './components/EventsToggle';
import { LayoutQuickToggle } from './components/LayoutQuickToggle';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useWakeLock } from './hooks/useWakeLock';
import { getUserLocation, isNearWindsor } from './utils/location';
import {
  loadLocalPrayerTimes,
  fetchPrayerTimesFromAPI,
  convertToPrayerDisplays,
  getNextPrayer,
  parseTimeToMinutes
} from './utils/prayerTimes';
import { requestNotificationPermission, showNotification, scheduleAllPrayerNotifications } from './utils/notifications';
import { PrayerDisplay, LayoutType } from './types/prayer';
import { getIslamicEvents, IslamicEvent } from './utils/islamicEvents';

function CRMRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-800 via-teal-700 to-teal-900 flex items-center justify-center">
        <div className="text-white text-2xl font-semibold animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return <CRMPage />;
}

function App() {
  const isCRMPage = window.location.pathname === '/crm';
  const isResetPasswordPage = window.location.pathname === '/reset-password';
  const isSharePage = window.location.pathname === '/share';

  if (isCRMPage) {
    return (
      <AuthProvider>
        <CRMRoute />
      </AuthProvider>
    );
  }

  if (isResetPasswordPage) {
    return (
      <AuthProvider>
        <ResetPasswordPage />
      </AuthProvider>
    );
  }

  if (isSharePage) {
    return (
      <ShareLandingPage
        onContinueToApp={() => {
          window.location.href = '/';
        }}
      />
    );
  }

  const [showShareLanding, setShowShareLanding] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('share') === 'true';
  });
  const [showPreferences, setShowPreferences] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('token');
  });
  const [preferencesToken, setPreferencesToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  });
  const [currentTime, setCurrentTime] = useState('');
  const [location, setLocation] = useState('Loading...');
  const [prayers, setPrayers] = useState<PrayerDisplay[]>([]);
  const [nextPrayer, setNextPrayer] = useState('fajr');
  const [timeUntilNext, setTimeUntilNext] = useState('');
  const [minutesUntilNext, setMinutesUntilNext] = useState<number>(999);
  const [layout, setLayout] = useState<LayoutType>(() => {
    try {
      const saved = localStorage.getItem('layout-preference');
      if (saved) {
        return JSON.parse(saved) as LayoutType;
      }
      // Default to 'table' on mobile devices, 'cards' on desktop
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
      return isMobile ? 'table' : 'cards';
    } catch {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
      return isMobile ? 'table' : 'cards';
    }
  });
  const [mutedPrayers, setMutedPrayers] = useState<Set<string>>(new Set());
  const [notifiedPrayers, setNotifiedPrayers] = useState<Set<string>>(new Set());
  const [is10MinAlert, setIs10MinAlert] = useState(false);
  const [maghribBoomEnabled, setMaghribBoomEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('maghrib-boom-enabled');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });
  const [showAllPrayers, setShowAllPrayers] = useState(false);
  const [showIslamicEvent, setShowIslamicEvent] = useState(false);
  const [pageTimer, setPageTimer] = useState(20);
  const [carouselRotation, setCarouselRotation] = useState(0);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const [rotationDirection, setRotationDirection] = useState<'left' | 'right'>('right');
  const [upcomingEvent, setUpcomingEvent] = useState<{ event: IslamicEvent; daysUntil: number } | null>(null);
  const [activeEvent, setActiveEvent] = useState<{ event: IslamicEvent; dayOfEvent: number; totalDays: number } | null>(null);
  const [manualEventToggle, setManualEventToggle] = useState(false);

  // ShareButton modal state - lifted to App level to persist across view changes
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareNameInput, setShareNameInput] = useState('');
  const [shareEmailInput, setShareEmailInput] = useState('');
  const [shareCityInput, setShareCityInput] = useState('');
  const [shareCountryInput, setShareCountryInput] = useState('');
  const [shareSubscribing, setShareSubscribing] = useState(false);
  const [shareSubscribed, setShareSubscribed] = useState(false);
  const [shareEmailError, setShareEmailError] = useState('');

  const nextPrayerRef = useRef<HTMLDivElement>(null);
  const pageTimerRef = useRef<NodeJS.Timeout | null>(null);
  const carouselTimerRef = useRef<NodeJS.Timeout | null>(null);
  useWakeLock();

  useEffect(() => {
    const handleFirstInteraction = () => {
      console.log('👆 First user interaction detected');
    };

    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('touchstart', handleFirstInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, []);

  const audioUnlocked = useRef(false);
  const audioTestInterval = useRef<NodeJS.Timeout | null>(null);

  const testAudioContext = useCallback(() => {
    const test = new Audio('/audio/ding.mp3');
    test.volume = 0.001;
    return test.play().then(() => {
      test.pause();
      test.currentTime = 0;
      return true;
    }).catch(() => {
      return false;
    });
  }, []);

  const unlockAudio = useCallback(() => {
    console.log('🔓 Attempting to unlock audio context');
    const testAudio = new Audio('/audio/ding.mp3');
    testAudio.volume = 0.01;
    testAudio.play().then(() => {
      testAudio.pause();
      testAudio.currentTime = 0;
      audioUnlocked.current = true;
      console.log('✅ Audio unlocked successfully');
    }).catch((err) => {
      console.log('⚠️ Audio unlock pending user interaction:', err.message);
      audioUnlocked.current = false;
    });
  }, []);

  useEffect(() => {
    const handler = () => {
      unlockAudio();
    };

    document.addEventListener('touchstart', handler);
    document.addEventListener('click', handler);
    document.addEventListener('keydown', handler);

    // Test audio context every 5 minutes and re-unlock if needed
    audioTestInterval.current = setInterval(() => {
      testAudioContext().then(success => {
        if (!success && audioUnlocked.current) {
          console.log('🔴 Audio context suspended, attempting re-unlock');
          audioUnlocked.current = false;
          unlockAudio();
        }
      });
    }, 300000);

    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('click', handler);
      document.removeEventListener('keydown', handler);
      if (audioTestInterval.current) {
        clearInterval(audioTestInterval.current);
      }
    };
  }, [unlockAudio, testAudioContext]);

  const sendEmailNotification = useCallback(async (
    prayerName: string,
    prayerTime: string,
    notificationType: string,
    nextPrayerName?: string,
    nextPrayerTime?: string
  ) => {
    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-prayer-notification`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prayerName,
          prayerTime,
          notificationType,
          location,
          nextPrayerName,
          nextPrayerTime
        })
      });

      if (!response.ok) {
        console.error('Failed to send email notification:', await response.text());
      } else {
        console.log(`📧 Email notification sent for ${prayerName} (${notificationType})`);
      }
    } catch (error) {
      console.error('Error sending email notification:', error);
    }
  }, [location]);

  const playAudioFile = useCallback((src: string): Promise<void> => {
    return new Promise((resolve) => {
      console.log(`🎵 Attempting to play audio: ${src}`);

      const audio = new Audio(src);
      audio.preload = 'auto';
      audio.volume = 1.0;
      let resolved = false;

      const cleanup = () => {
        if (!resolved) {
          resolved = true;
          audio.onended = null;
          audio.onerror = null;
          audio.oncanplaythrough = null;
          audio.pause();
          audio.src = '';
        }
      };

      audio.onended = () => {
        console.log(`✅ Audio finished playing: ${src}`);
        cleanup();
        resolve();
      };

      audio.onerror = (e) => {
        console.error('❌ Audio failed to load:', src, e);
        console.error('❌ Audio error details:', {
          src,
          error: e,
          readyState: audio.readyState,
          networkState: audio.networkState,
          duration: audio.duration
        });
        cleanup();
        resolve();
      };

      audio.oncanplaythrough = () => {
        console.log(`📦 Audio ready to play: ${src}`);
        audio.play().then(() => {
          console.log(`▶️ Audio started playing: ${src}`);
        }).catch((error) => {
          console.error('❌ Audio play failed:', src, error);
          if (error.name === 'NotAllowedError') {
            console.log('🔴 Audio context suspended! Attempting recovery...');
            audioUnlocked.current = false;
            unlockAudio();
            console.log('📣 Please interact with the app (tap/click) to resume audio');
          } else if (!audioUnlocked.current) {
            console.log('⚠️ Audio not unlocked. User interaction may be required.');
          }
          cleanup();
          resolve();
        });
      };

      audio.load();

      // Force cleanup after 5 minutes to prevent memory leaks
      setTimeout(() => {
        if (!resolved) {
          console.log(`⏱️ Timeout for ${src}`);
          cleanup();
          resolve();
        }
      }, 300000);
    });
  }, []);

  const requestLocationPermission = useCallback(async () => {
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        console.log('Geolocation permission:', result.state);

        if (result.state === 'prompt') {
          console.log('Requesting geolocation permission...');
        }
      } catch (error) {
        console.log('Permissions API not fully supported');
      }
    }
  }, []);

  const loadPrayerTimes = useCallback(async () => {
    try {
      await requestLocationPermission();

      console.log('Attempting to get user location...');
      const userLoc = await getUserLocation();

      if (userLoc && isNearWindsor(userLoc.lat, userLoc.lng)) {
        console.log('User is near Windsor:', userLoc);
        setLocation(`Windsor, ON`);
        const localTimes = await loadLocalPrayerTimes(new Date());
        if (localTimes) {
          const displays = convertToPrayerDisplays(localTimes);
          setPrayers(displays);
          return;
        }
      }

      if (userLoc) {
        console.log('Using user location:', userLoc);
        setLocation(userLoc.city);
        const apiTimes = await fetchPrayerTimesFromAPI(userLoc.lat, userLoc.lng, new Date());
        if (apiTimes) {
          const displays = convertToPrayerDisplays(apiTimes);
          setPrayers(displays);
          return;
        }
      }

      setLocation('Windsor, ON');
      const fallbackTimes = await loadLocalPrayerTimes(new Date());
      if (fallbackTimes) {
        const displays = convertToPrayerDisplays(fallbackTimes);
        setPrayers(displays);
      }
    } catch (error) {
      console.error('Error loading prayer times:', error);
    }
  }, [requestLocationPermission]);

  useEffect(() => {
    console.log('App mounted, requesting location...');
    console.log(`💥 Initial Maghrib Boom State: ${maghribBoomEnabled}`);
    console.log(`💥 LocalStorage value: ${localStorage.getItem('maghrib-boom-enabled')}`);
    loadPrayerTimes();

    const savedLayout = localStorage.getItem('prayerLayout') as LayoutType;
    if (savedLayout) {
      setLayout(savedLayout);
    }

    // DISABLED: Browser notifications can cause Android to throttle/block the app
    // requestNotificationPermission().then(granted => {
    //   if (granted) {
    //     console.log('Notification permission granted');
    //   }
    // });
  }, [loadPrayerTimes, maghribBoomEnabled]);

  // DISABLED: Using only audio alerts, no browser notifications
  // useEffect(() => {
  //   // Schedule all notifications when prayers are loaded
  //   if (prayers.length > 0) {
  //     scheduleAllPrayerNotifications(prayers);
  //   }
  // }, [prayers]);

  useEffect(() => {
    async function checkIslamicEvents() {
      const now = new Date();
      const events = await getIslamicEvents();

      let foundUpcoming = false;
      let foundActive = false;

      for (const event of events) {
        // Check for active Ramadan FIRST
        if (event.type === 'ramadan' && event.name === 'Ramadan Begins') {
          const ramadanStart = new Date(event.date);
          const ramadanEnd = new Date(ramadanStart.getTime() + 30 * 24 * 60 * 60 * 1000);

          if (now >= ramadanStart && now <= ramadanEnd) {
            const dayOfRamadan = Math.floor((now.getTime() - ramadanStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
            setActiveEvent({ event, dayOfEvent: dayOfRamadan, totalDays: 30 });
            foundActive = true;
            break;
          }
        }

        // Only check for upcoming events if we're not in an active period
        if (!foundActive) {
          const eventDate = new Date(event.date);
          const timeDiff = eventDate.getTime() - now.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

          if (daysDiff >= 1 && daysDiff <= 5) {
            setUpcomingEvent({ event, daysUntil: daysDiff });
            foundUpcoming = true;
            break;
          }
        }
      }

      if (!foundUpcoming) setUpcomingEvent(null);
      if (!foundActive) setActiveEvent(null);
    }

    checkIslamicEvents();
    const interval = setInterval(checkIslamicEvents, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const clearOldNotifications = () => {
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();

      setNotifiedPrayers(prev => {
        const newSet = new Set<string>();
        prev.forEach(key => {
          const parts = key.split('-');
          const prayerKey = parts[0];
          const prayer = prayers.find(p => p.key === prayerKey);

          if (prayer) {
            const prayerMinutes = parseTimeToMinutes(prayer.time);
            // FIXED: Keep only if prayer is upcoming (not if it's old)
            // Keep if prayer hasn't happened yet OR if it happened less than 5 minutes ago
            if (prayerMinutes > currentMinutes || (prayerMinutes >= currentMinutes - 5 && prayerMinutes <= currentMinutes)) {
              newSet.add(key);
            } else {
              console.log(`🗑️ Removing old notification: ${key}`);
            }
          }
        });
        console.log(`🧹 Notification cleanup: ${prev.size} → ${newSet.size}`);
        return newSet;
      });
    };

    // Run cleanup every 10 minutes instead of every hour
    const cleanupInterval = setInterval(clearOldNotifications, 600000);

    // Also run cleanup on component mount
    clearOldNotifications();

    return () => clearInterval(cleanupInterval);
  }, [prayers]);

  useEffect(() => {
    const checkAndRefreshPrayerTimes = () => {
      const now = new Date();
      if (now.getHours() === 0 && now.getMinutes() === 0) {
        console.log('🔄 Midnight refresh - reloading prayer times');
        setNotifiedPrayers(new Set());
        loadPrayerTimes();
        audioUnlocked.current = false;
        unlockAudio();
      }
    };

    const handleClearNotifiedPrayers = () => {
      console.log('🗑️ Manually clearing notified prayers from debug panel');
      setNotifiedPrayers(new Set());
    };

    const midnightCheckInterval = setInterval(checkAndRefreshPrayerTimes, 60000);
    window.addEventListener('clear-notified-prayers', handleClearNotifiedPrayers);

    return () => {
      clearInterval(midnightCheckInterval);
      window.removeEventListener('clear-notified-prayers', handleClearNotifiedPrayers);
    };
  }, [loadPrayerTimes, unlockAudio]);

  useEffect(() => {
    if (!carouselPaused) {
      const timer = setInterval(() => {
        setPageTimer(prev => {
          if (prev <= 1) {
            const hasEvent = activeEvent || upcomingEvent;

            if (hasEvent) {
              if (!showIslamicEvent && !showAllPrayers) {
                setShowIslamicEvent(true);
                // Show active Ramadan content for 60 seconds, upcoming events for 30 seconds
                return activeEvent ? 60 : 30;
              } else if (showIslamicEvent && !showAllPrayers) {
                setShowIslamicEvent(false);
                setShowAllPrayers(true);
                return 20;
              } else {
                setShowAllPrayers(false);
                setShowIslamicEvent(false);
                return 20;
              }
            } else {
              setShowAllPrayers(curr => !curr);
              return showAllPrayers ? 20 : 15;
            }
          }
          return prev - 1;
        });
      }, 1000);
      pageTimerRef.current = timer;

      return () => clearInterval(timer);
    }
  }, [carouselPaused, showAllPrayers, showIslamicEvent, activeEvent, upcomingEvent]);

  useEffect(() => {
    if (layout === 'cards' && !showAllPrayers && nextPrayerRef.current) {
      setTimeout(() => {
        nextPrayerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [nextPrayer, layout, showAllPrayers]);

  const checkNotifications = useCallback((now: Date) => {
    if (prayers.length === 0) {
      console.log('⚠️ No prayers loaded, skipping notification check');
      return;
    }

    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const currentSeconds = now.getSeconds();
    let alertUpdated = false;

    const getNextPrayerInfo = (currentPrayerKey: string) => {
      const prayerOrder = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
      const currentIndex = prayerOrder.indexOf(currentPrayerKey);
      if (currentIndex === -1 || currentIndex === prayerOrder.length - 1) {
        const fajrPrayer = prayers.find(p => p.key === 'fajr');
        return fajrPrayer ? { name: fajrPrayer.name, time: fajrPrayer.time } : undefined;
      }
      const nextPrayerKey = prayerOrder[currentIndex + 1];
      const nextPrayerData = prayers.find(p => p.key === nextPrayerKey);
      return nextPrayerData ? { name: nextPrayerData.name, time: nextPrayerData.time } : undefined;
    };

    prayers.forEach(prayer => {
      if (prayer.key === 'sunrise') return;
      if (mutedPrayers.has(prayer.key)) return;
      if (!prayer.time) return;

      const prayerMinutes = parseTimeToMinutes(prayer.time);
      const diff = prayerMinutes - currentMinutes;

      const notifKey20 = `${prayer.key}-20`;
      const notifKey10 = `${prayer.key}-10`;
      const notifKeyExact = `${prayer.key}-exact`;

      // Log current state every 10 seconds for debugging
      if (prayer.key === nextPrayer && currentSeconds % 10 === 0) {
        console.log(`📊 [${now.toLocaleTimeString()}] ${prayer.name}: diff=${diff}min, seconds=${currentSeconds}, muted=${mutedPrayers.has(prayer.key)}`);
      }

      const nextPrayerInfo = getNextPrayerInfo(prayer.key);

      if (diff === 20 && currentSeconds < 5 && !notifiedPrayers.has(notifKey20)) {
        console.log(`🔔 20 min notification for ${prayer.name}`);
        playAudioFile('/audio/ding.mp3');
        sendEmailNotification(
          prayer.name,
          prayer.time,
          '20min',
          nextPrayerInfo?.name,
          nextPrayerInfo?.time
        );
        setNotifiedPrayers(prev => new Set(prev).add(notifKey20));
      } else if (diff === 10 && currentSeconds < 5 && !notifiedPrayers.has(notifKey10)) {
        console.log(`🔔 10 min notification for ${prayer.name}`);
        playAudioFile('/audio/ding.mp3');
        sendEmailNotification(
          prayer.name,
          prayer.time,
          '10min',
          nextPrayerInfo?.name,
          nextPrayerInfo?.time
        );
        setNotifiedPrayers(prev => new Set(prev).add(notifKey10));
      }

      if (diff <= 5 && diff > 0 && !alertUpdated) {
        setIs10MinAlert(true);
        alertUpdated = true;
      } else if (diff > 5 && !alertUpdated) {
        setIs10MinAlert(false);
        alertUpdated = true;
      }

      if (diff === 0 && currentSeconds < 5 && !notifiedPrayers.has(notifKeyExact)) {
        console.log(`🔔 EXACT TIME notification for ${prayer.name}`);
        console.log(`📊 Prayer Key: ${prayer.key}`);
        console.log(`📊 Maghrib Boom Enabled State: ${maghribBoomEnabled}`);
        console.log(`📊 Is this Maghrib? ${prayer.key === 'maghrib'}`);
        console.log(`📊 Should play boom? ${prayer.key === 'maghrib' && maghribBoomEnabled}`);
        if (!alertUpdated) {
          setIs10MinAlert(false);
          alertUpdated = true;
        }
        sendEmailNotification(
          prayer.name,
          prayer.time,
          'exact',
          nextPrayerInfo?.name,
          nextPrayerInfo?.time
        );
        const playSequence = async () => {
          console.log(`🔊 Playing audio for ${prayer.key}`);
          if (prayer.key === 'fajr') {
            console.log('▶️ Playing Fajr sequence');
            await playAudioFile('/audio/fajar.mp3');
            await playAudioFile('/audio/doaa.mp3');
          } else if (prayer.key === 'maghrib' && maghribBoomEnabled) {
            console.log('💥 MAGHRIB BOOM ENABLED - Playing boom sequence');
            console.log('▶️ Step 1: Playing boom.wav (1st time)');
            await playAudioFile('/audio/boom.wav');
            console.log('▶️ Step 2: Playing boom.wav (2nd time)');
            await playAudioFile('/audio/boom.wav');
            console.log('▶️ Step 3: Playing boom.wav (3rd time)');
            await playAudioFile('/audio/boom.wav');
            console.log('▶️ Step 4: Playing azan.mp3');
            await playAudioFile('/audio/azan.mp3');
            console.log('▶️ Step 5: Playing doaa.mp3');
            await playAudioFile('/audio/doaa.mp3');
          } else {
            console.log(`▶️ Playing standard sequence for ${prayer.key} (boom: ${maghribBoomEnabled})`);
            await playAudioFile('/audio/azan.mp3');
            await playAudioFile('/audio/doaa.mp3');
          }
          console.log(`✅ Audio sequence completed for ${prayer.key}`);
        };
        playSequence();
        setNotifiedPrayers(prev => new Set(prev).add(notifKeyExact));
      }
    });
  }, [prayers, mutedPrayers, notifiedPrayers, maghribBoomEnabled, playAudioFile, sendEmailNotification]);

  useEffect(() => {
    let frameId: number;

    const updateTime = () => {
      const now = new Date();
      const currentMinute = now.getMinutes();

      let hours = now.getHours();
      const hours12 = hours % 12 || 12;
      const minutes = String(currentMinute).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const timeStr = `${hours12}:${minutes}:${seconds}`;
      setCurrentTime(timeStr);

      if (prayers.length > 0) {
        const next = getNextPrayer(prayers, now);
        setNextPrayer(next);

        const nextPrayerObj = prayers.find(p => p.key === next);
        if (nextPrayerObj) {
          const currentMinutes = now.getHours() * 60 + currentMinute;
          const prayerMinutes = parseTimeToMinutes(nextPrayerObj.time, nextPrayerObj.key);
          let diff = prayerMinutes - currentMinutes;

          if (diff < 0) {
            diff += 24 * 60;
          }

          const hours = Math.floor(diff / 60);
          const minutes = diff % 60;

          setMinutesUntilNext(diff);

          if (hours > 0) {
            setTimeUntilNext(`${hours}h ${minutes}m`);
          } else {
            setTimeUntilNext(`${minutes}m`);
          }
        }

        checkNotifications(now);
      }

      frameId = requestAnimationFrame(updateTime);
    };

    updateTime();
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [prayers, checkNotifications]);

  const handleLayoutChange = (newLayout: LayoutType) => {
    setLayout(newLayout);
    localStorage.setItem('layout-preference', JSON.stringify(newLayout));
  };

  const toggleMute = (prayerKey: string) => {
    setMutedPrayers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(prayerKey)) {
        newSet.delete(prayerKey);
      } else {
        newSet.add(prayerKey);
      }
      return newSet;
    });
  };

  const toggleMaghribBoom = () => {
    setMaghribBoomEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('maghrib-boom-enabled', JSON.stringify(newValue));
      console.log(`💥 Maghrib Boom toggled to: ${newValue}`);
      return newValue;
    });
  };

  const toggle3DCards = () => {
    setLayout(prev => {
      const newLayout = prev === 'cards' ? 'table' : 'cards';
      localStorage.setItem('layout-preference', JSON.stringify(newLayout));
      return newLayout;
    });
  };

  const handleCarouselClick = () => {
    setCarouselPaused(prev => !prev);
  };

  const handleSwipeLeft = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCarouselPaused(false);
    setRotationDirection('left');
  };

  const handleSwipeRight = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCarouselPaused(false);
    setRotationDirection('right');
  };

  useEffect(() => {
    if (prayers.length === 0 || layout !== 'cards' || carouselPaused) return;

    const nextPrayerIndex = prayers.findIndex(p => p.key === nextPrayer);
    const anglePerCard = 360 / prayers.length;
    let currentIndex = nextPrayerIndex;

    const rotateToNextCard = () => {
      setCarouselRotation(prev => {
        const direction = rotationDirection === 'right' ? -1 : 1;
        const newRotation = prev + (direction * anglePerCard);

        currentIndex = (currentIndex + (rotationDirection === 'right' ? 1 : -1) + prayers.length) % prayers.length;

        if (carouselTimerRef.current) {
          clearInterval(carouselTimerRef.current);
        }

        const isNextPrayerCard = prayers[currentIndex]?.key === nextPrayer;
        const pauseDuration = isNextPrayerCard ? 5000 : 2000;

        carouselTimerRef.current = setInterval(rotateToNextCard, pauseDuration);

        return newRotation;
      });
    };

    const startRotation = () => {
      const initialRotation = -nextPrayerIndex * anglePerCard;
      setCarouselRotation(initialRotation);

      const pauseDuration = 5000;
      const timer = setInterval(rotateToNextCard, pauseDuration);
      carouselTimerRef.current = timer;

      return timer;
    };

    const timer = startRotation();

    return () => {
      if (carouselTimerRef.current) {
        clearInterval(carouselTimerRef.current);
      }
    };
  }, [prayers, nextPrayer, layout, carouselPaused, rotationDirection]);

  if (showPreferences && preferencesToken) {
    return (
      <PreferencesPage
        token={preferencesToken}
        onClose={() => {
          setShowPreferences(false);
          const url = new URL(window.location.href);
          url.searchParams.delete('token');
          window.history.replaceState({}, '', url.toString());
        }}
      />
    );
  }

  if (showShareLanding) {
    return (
      <ShareLandingPage
        onContinueToApp={() => {
          setShowShareLanding(false);
          const url = new URL(window.location.href);
          url.searchParams.delete('share');
          window.history.replaceState({}, '', url.toString());
        }}
      />
    );
  }

  if (prayers.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-900 flex items-center justify-center">
        <div className="text-white text-2xl font-semibold animate-pulse">Loading prayer times...</div>
      </div>
    );
  }

  // Check if there's an active or upcoming event
  const hasActiveEvent = !!(activeEvent || upcomingEvent);

  // Render ShareButton at the top level so state persists across view changes
  const shareButtonComponent = (
    <ShareButton
      showModal={showShareModal}
      setShowModal={setShowShareModal}
      nameInput={shareNameInput}
      setNameInput={setShareNameInput}
      emailInput={shareEmailInput}
      setEmailInput={setShareEmailInput}
      cityInput={shareCityInput}
      setCityInput={setShareCityInput}
      countryInput={shareCountryInput}
      setCountryInput={setShareCountryInput}
      subscribing={shareSubscribing}
      setSubscribing={setShareSubscribing}
      subscribed={shareSubscribed}
      setSubscribed={setShareSubscribed}
      emailError={shareEmailError}
      setEmailError={setShareEmailError}
    />
  );

  // Show single prayer view when 4 minutes or less until next prayer
  const nextPrayerObj = prayers.find(p => p.key === nextPrayer);
  if (minutesUntilNext <= 4 && nextPrayerObj) {
    return (
      <>
        {shareButtonComponent}
        <SinglePrayerView prayer={nextPrayerObj} timeUntilNext={timeUntilNext} />
      </>
    );
  }

  if (showIslamicEvent && activeEvent) {
    return (
      <>
        <ActiveEventDaily event={activeEvent.event} dayOfEvent={activeEvent.dayOfEvent} totalDays={activeEvent.totalDays} prayers={prayers} />
      </>
    );
  }

  if (showIslamicEvent && upcomingEvent && !activeEvent) {
    return (
      <>
        <EventCountdown event={upcomingEvent.event} daysUntil={upcomingEvent.daysUntil} prayers={prayers} />
      </>
    );
  }

  if (showAllPrayers) {
    return (
      <>
        <EventsToggle
          showEvents={manualEventToggle}
          onToggle={() => setManualEventToggle(!manualEventToggle)}
          hasActiveEvent={hasActiveEvent}
        />
        {shareButtonComponent}
        <AllPrayersList prayers={prayers} nextPrayer={nextPrayer} timeRemaining={pageTimer} currentTime={currentTime} layout={layout} onLayoutChange={handleLayoutChange} location={location} timeUntilNext={timeUntilNext} maghribBoomEnabled={maghribBoomEnabled} onToggleBoom={toggleMaghribBoom} />
      </>
    );
  }

  // Mobile layout - full screen optimized for mobile
  if (layout === 'mobile') {
    return (
      <>
        <InstallPrompt />
        <NotificationPermission />
        <KioskControls />
        <EventsToggle
          showEvents={manualEventToggle}
          onToggle={() => setManualEventToggle(!manualEventToggle)}
          hasActiveEvent={hasActiveEvent}
        />
        {shareButtonComponent}
        <MobileView
          prayers={prayers}
          nextPrayer={nextPrayer}
          timeUntilNext={timeUntilNext}
          mutedPrayers={mutedPrayers}
          onToggleMute={toggleMute}
          currentTime={currentTime}
          location={location}
          maghribBoomEnabled={maghribBoomEnabled}
          onToggleBoom={toggleMaghribBoom}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-900 py-8 px-4">
      <InstallPrompt />
      <NotificationPermission />
      <KioskControls />
      <LayoutQuickToggle layout={layout} onToggle={toggle3DCards} />
      <EventsToggle
        showEvents={manualEventToggle}
        onToggle={() => setManualEventToggle(!manualEventToggle)}
        hasActiveEvent={hasActiveEvent}
      />
      {shareButtonComponent}

      <div className="max-w-7xl mx-auto">
        <Header currentTime={currentTime} location={location} layout={layout} onLayoutChange={handleLayoutChange} pageTimer={pageTimer} />

        {layout === 'cards' ? (
          <div className="relative w-full h-[65vh] sm:h-[70vh] md:h-[75vh] lg:h-[800px] flex items-center justify-center perspective-container">
            <div
              className="carousel-wheel"
              style={{
                transform: `rotateY(${carouselRotation}deg)`,
                animationPlayState: carouselPaused ? 'paused' : 'running'
              }}
              onClick={handleCarouselClick}
            >
              {prayers.map((prayer, index) => {
                const angle = (360 / prayers.length) * index;
                return (
                  <div
                    key={prayer.key}
                    ref={prayer.key === nextPrayer ? nextPrayerRef : null}
                    className="carousel-item"
                    style={{
                      '--rotation': `${angle}deg`,
                      '--index': index,
                    } as React.CSSProperties}
                  >
                    <PrayerCard
                      prayer={prayer}
                      isNext={prayer.key === nextPrayer}
                      timeUntilNext={prayer.key === nextPrayer ? timeUntilNext : ''}
                      isMuted={mutedPrayers.has(prayer.key)}
                      onToggleMute={() => toggleMute(prayer.key)}
                      is10MinAlert={is10MinAlert && nextPrayer === prayer.key}
                      showBoomToggle={prayer.key === 'maghrib'}
                      boomEnabled={maghribBoomEnabled}
                      onToggleBoom={toggleMaghribBoom}
                    />
                  </div>
                );
              })}
            </div>

            <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 sm:gap-4 z-50">
              <button
                onClick={handleSwipeLeft}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 rounded-full text-sm sm:text-base font-semibold shadow-lg transition-all"
              >
                <span className="hidden sm:inline">← Rotate Left</span>
                <span className="sm:hidden">←</span>
              </button>
              <button
                onClick={handleSwipeRight}
                className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-3 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 rounded-full text-sm sm:text-base font-semibold shadow-lg transition-all"
              >
                <span className="hidden sm:inline">Rotate Right →</span>
                <span className="sm:hidden">→</span>
              </button>
            </div>
          </div>
        ) : (
          <PrayerTable
            prayers={prayers}
            nextPrayer={nextPrayer}
            mutedPrayers={mutedPrayers}
            onToggleMute={toggleMute}
            timeUntilNext={timeUntilNext}
          />
        )}

        <div className="mt-8 text-center text-white/70 text-sm">
          <p>May Allah accept your prayers</p>
        </div>
      </div>
    </div>
  );
}

export default App;
